import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, StyleProp, ViewStyle } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaRadius } from '../../styles/theme';
import type { RecipeAdjustment } from '../../hooks/useRecipeAdjustment';

interface AdjustedChipProps {
  adjustment: RecipeAdjustment;
  /** Base servings, so the chip can state "4 → 6". */
  baseServings: number;
  servings: number;
  /** Restores the base servings. Omit where servings are not resettable. */
  onResetServings?: () => void;
  style?: StyleProp<ViewStyle>;
  /** Compact single-line variant for the cooking-mode top bar. */
  variant?: 'chip' | 'inline';
}

/**
 * States the whole transform in one line — "Ajusté · 4 → 6 portions · métrique"
 * — with one-tap undo.
 *
 * This is the layer that makes the change legible at a glance; the inline marks
 * make each individual number legible. Renders nothing when the recipe is shown
 * exactly as written, so its presence alone means "this was modified".
 */
export const AdjustedChip: React.FC<AdjustedChipProps> = ({
  adjustment,
  baseServings,
  servings,
  onResetServings,
  style,
  variant = 'chip',
}) => {
  const { t } = useTranslation();
  const { isAdjusted, servingsChanged, systemChanged, system, resetSystem } = adjustment;

  if (!isAdjusted) return null;

  const parts: string[] = [];
  if (servingsChanged) {
    parts.push(t('recipeDetail.adjustedServings', {
      defaultValue: '{{from}} → {{to}} portions',
      from: baseServings,
      to: servings,
    }));
  }
  if (systemChanged) {
    parts.push(system === 'metric' ? t('recipeDetail.metric') : t('recipeDetail.imperial'));
  }

  const handleReset = () => {
    if (systemChanged) resetSystem();
    if (servingsChanged) onResetServings?.();
  };

  const label = `${t('recipeDetail.adjusted', 'Ajusté')} · ${parts.join(' · ')}`;
  const inline = variant === 'inline';

  return (
    <View style={[inline ? styles.inline : styles.chip, style]}>
      <Text style={[styles.label, inline && styles.labelInline]} numberOfLines={1}>
        {label}
      </Text>
      <TouchableOpacity
        onPress={handleReset}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        accessibilityRole="button"
        accessibilityLabel={t('recipeDetail.resetToOriginal', 'Rétablir la recette originale')}
      >
        <Text style={[styles.reset, inline && styles.labelInline]}>
          {t('recipeDetail.reset', 'rétablir')}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    alignSelf: 'flex-start',
    paddingVertical: 5,
    paddingHorizontal: 11,
    borderRadius: PaprikaRadius.full,
    backgroundColor: Paprika.brandTint,
    borderWidth: 1,
    borderColor: Paprika.brandSoft,
  },
  inline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  label: {
    fontFamily: 'Inter-Medium',
    fontWeight: '500',
    fontSize: 11.5,
    color: Paprika.accent,
    flexShrink: 1,
  },
  labelInline: {
    fontSize: 11,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  reset: {
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    fontSize: 11.5,
    color: Paprika.accent,
    textDecorationLine: 'underline',
  },
});

export default AdjustedChip;
